import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, TrendingUp, Wallet, FileMinus, FilePlus, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';
import { LocationLine } from '../../../components/LocationLine';

const money = (n) => `${n < 0 ? '-' : ''}$${Math.abs(n || 0).toFixed(2)}`;
const isoDay = (d) => d.toISOString().slice(0, 10);

const LINES = [
  {
    key: 'delivered_and_earned',
    basis: 'delivered',
    label: 'Delivered & earned',
    hint: 'Recognized when the order was delivered',
    icon: TrendingUp,
    accent: 'text-emerald-600',
  },
  {
    key: 'advance_cash_received',
    basis: 'advance_cash',
    label: 'Advance cash received',
    hint: 'Advance invoices paid before delivery',
    icon: Wallet,
    accent: 'text-sky-600',
  },
  {
    key: 'credit_memos_issued',
    basis: 'credit_memos',
    label: 'Credit memos issued',
    hint: 'Reconciliation credits raised this period',
    icon: FileMinus,
    accent: 'text-red-600',
  },
  {
    key: 'supplemental_invoices',
    basis: 'supplemental',
    label: 'Supplemental invoices',
    hint: 'Approved order increases invoiced separately',
    icon: FilePlus,
    accent: 'text-amber-600',
  },
];

const RevenueReport = () => {
  const navigate = useNavigate();
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

  const [start, setStart] = useState(isoDay(monthStart));
  const [end, setEnd] = useState(isoDay(today));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);
  const [drillRows, setDrillRows] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReport(await adminService.getRevenueReport({ start, end }));
      setDrill(null);
      setDrillRows([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load the revenue report');
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const openDrill = async (line) => {
    if (drill === line.basis) { setDrill(null); setDrillRows([]); return; }
    setDrill(line.basis);
    try {
      setDrillRows(await adminService.getRevenueReportLines(line.basis, { start, end }));
    } catch {
      setDrillRows([]);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-revenue-report-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/reports')} data-testid="back-to-reports-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Revenue</h2>
          <p className="text-sm text-gray-500 mt-1">
            Each invoice is recognized exactly once — advance cash in the period it was paid,
            everything else in the period it was delivered.
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 flex flex-col sm:flex-row gap-4 sm:items-end">
          <div className="flex-1">
            <Label className="text-xs uppercase text-gray-500">From</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 min-h-[44px]" data-testid="revenue-start-date" />
          </div>
          <div className="flex-1">
            <Label className="text-xs uppercase text-gray-500">To</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 min-h-[44px]" data-testid="revenue-end-date" />
          </div>
          <Button className="bg-amber-500 hover:bg-amber-600 min-h-[44px]" onClick={load} data-testid="revenue-apply-btn">
            Apply
          </Button>
        </CardContent>
      </Card>

      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
        </div>
      )}

      {!loading && report && (
        <>
          <div className="space-y-3" data-testid="revenue-lines">
            {LINES.map((line) => {
              const data = report[line.key] || { amount: 0, line_count: 0 };
              const Icon = line.icon;
              return (
                <Card key={line.key} className="hover:shadow-sm transition-shadow">
                  <CardContent className="p-4">
                    <button
                      type="button"
                      className="w-full flex items-center gap-4 text-left min-h-[44px]"
                      onClick={() => openDrill(line)}
                      data-testid={`revenue-line-${line.basis}`}
                    >
                      <Icon className={`h-5 w-5 shrink-0 ${line.accent}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900">{line.label}</p>
                        <p className="text-xs text-gray-500">{line.hint}</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-bold ${line.accent}`} data-testid={`revenue-amount-${line.basis}`}>
                          {money(data.amount)}
                        </p>
                        <p className="text-xs text-gray-400">{data.line_count} invoices</p>
                      </div>
                    </button>
                    {drill === line.basis && (
                      <div className="mt-3 border-t pt-3 overflow-x-auto" data-testid={`revenue-drill-${line.basis}`}>
                        {drillRows.length === 0 ? (
                          <p className="text-sm text-gray-500">Nothing in this period.</p>
                        ) : (
                          <table className="w-full text-sm">
                            <thead className="text-xs text-gray-500 uppercase">
                              <tr>
                                <th className="text-left p-2">Document</th>
                                <th className="text-left p-2">Client</th>
                                <th className="text-left p-2">Location</th>
                                <th className="text-left p-2">Order</th>
                                <th className="text-right p-2">Amount</th>
                                <th className="text-right p-2">Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {drillRows.map((row) => (
                                <tr key={row.id} className="border-t">
                                  <td className="p-2 font-medium">{row.document_number}</td>
                                  <td className="p-2">{row.client_name}</td>
                                  <td className="p-2">
                                    <LocationLine
                                      name={row.location_name}
                                      source={row.location_name_source}
                                      clientName={row.client_name}
                                      testId={`revenue-row-location-${row.id}`}
                                    />
                                  </td>
                                  <td className="p-2 text-gray-500">{row.order_number}</td>
                                  <td className="p-2 text-right">{money(row.amount)}</td>
                                  <td className="p-2 text-right text-gray-500">
                                    {row.date ? new Date(row.date).toLocaleDateString() : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card className="border-2 border-gray-900">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Total revenue</p>
                <p className="text-xs text-gray-400 mt-1">
                  Delivered + advance cash + supplementals − credits
                </p>
              </div>
              <p className="text-3xl font-bold text-gray-900" data-testid="revenue-total">
                {money(report.total_revenue)}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gray-50 border-dashed">
            <CardContent className="p-4">
              <button
                type="button"
                className="w-full flex items-start gap-3 text-left min-h-[44px]"
                onClick={() => openDrill({ basis: 'memo_advance_realized' })}
                data-testid="revenue-line-memo_advance_realized"
              >
                <Info className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-700">
                    Previously-recognized advance revenue realized this period
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Informational — already counted when the client paid, excluded from the total above.
                  </p>
                </div>
                <p className="text-lg font-semibold text-gray-500" data-testid="revenue-amount-memo">
                  {money(report.memo_advance_realized_this_period?.amount)}
                </p>
              </button>
              {drill === 'memo_advance_realized' && drillRows.length > 0 && (
                <div className="mt-3 border-t pt-3 space-y-1" data-testid="revenue-drill-memo">
                  {drillRows.map((row) => (
                    <p key={row.id} className="text-sm text-gray-600">
                      {row.document_number} · {row.client_name} · {money(row.amount)}
                    </p>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default RevenueReport;
